import React, { useState } from 'react';
import { 
  User, GraduationCap, Award, BookOpen, 
  ShieldCheck, QrCode, Lock, CheckCircle2, AlertCircle, 
  Download, Printer, Share2, ExternalLink, Calendar, 
  Phone, Mail, MapPin, Building2, Sparkles, X, ChevronRight,
  Clock, FileText, Check, ShieldAlert, Key, Smartphone
} from 'lucide-react';
import { NairaSign } from '../NairaSign';
import { DEMO_STUDENTS } from '../../data/portalData';

export const CandidateAccountDrawer = ({ 
  isOpen, 
  onClose, 
  studentId = 'student-dba',
  onSelectStudent,
  onLogout 
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState('profile'); // profile, bursary, grades, supervisor, idcard, security
  const [copiedToken, setCopiedToken] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [whatsappAlerts, setWhatsappAlerts] = useState(true);

  if (!isOpen) return null;

  const student = DEMO_STUDENTS.find(s => s.id === studentId) || DEMO_STUDENTS[0];
  const candidateName = student.fullName || student.name || 'Emem Udoh';
  const candidateProgramme = student.programmeName || student.programme || 'Doctor of Business Administration (DBA)';
  const candidateLevel = student.degreeLevel || 'DBA';
  const candidateTerm = student.currentTerm || 'Term 1 (T1)';
  const candidatePhone = student.phone || '+234 802 443 1120';
  const candidateEmail = student.email || 'e.udoh@student.aksu.edu.ng';

  const submenus = [
    { id: 'profile', label: '1. Academic Profile & Identity', icon: User, badge: 'Senate Roll' },
    { id: 'bursary', label: '2. Bursary & Tuition Clearance', icon: NairaSign, badge: '100% Cleared' },
    { id: 'grades', label: '3. Academic Records & Grades', icon: Award, badge: 'GPA 4.85' },
    { id: 'supervisor', label: '4. Dissertation & Supervisory Desk', icon: BookOpen, badge: 'Milestones' },
    { id: 'idcard', label: '5. Digital ID Card & QR Pass', icon: QrCode, badge: 'Smart Gate' },
    { id: 'security', label: '6. Security, 2FA & Privacy', icon: Lock, badge: 'RBAC Tier-3' }
  ];

  const handleCopyQRToken = () => {
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end animate-in fade-in duration-200">
      {/* Backdrop */}
      <div 
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity cursor-pointer" 
      />

      {/* Slide-out Drawer */}
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col z-10 animate-in slide-in-from-right duration-300 border-l-4 border-[#7A470E]">
        
        {/* Top Header Strip */}
        <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white p-4 sm:p-6 border-b-2 border-[#7A470E] shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="relative">
                <img 
                  src={student.avatar} 
                  alt={candidateName} 
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-[#B87A28] shadow-md"
                />
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-emerald-500 rounded-full border-2 border-white shadow-xs" title="Bursary & Senate Cleared" />
              </div>

              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-base sm:text-xl font-black font-sc text-white leading-tight">
                    {candidateName}
                  </h2>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-500/50 font-bold">
                    Active Scholar
                  </span>
                </div>
                <div className="text-xs text-amber-300 font-mono mt-0.5 font-bold">
                  {student.matricNo}
                </div>
                <div className="text-xs text-amber-100 font-sc mt-0.5 font-semibold">
                  {candidateProgramme} · {candidateTerm}
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition-colors cursor-pointer"
              title="Close Account Drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Persona Switcher for Evaluators */}
          <div className="mt-4 pt-3 border-t border-slate-700/80 flex items-center justify-between text-xs">
            <span className="text-[10px] uppercase font-bold text-amber-300 font-mono tracking-wider">
              Switch Candidate Persona:
            </span>
            <div className="flex gap-1.5 overflow-x-auto">
              {DEMO_STUDENTS.map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    if (onSelectStudent) onSelectStudent(s.id);
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
                    s.id === studentId 
                      ? 'bg-[#7A470E] text-white border border-amber-300 shadow-xs' 
                      : 'bg-[#030F1C] text-slate-300 hover:text-white border border-slate-700'
                  }`}
                >
                  {s.id.replace('student-', '').toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Submenu Tabs Navigation */}
        <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 flex gap-1.5 overflow-x-auto shrink-0 no-scrollbar">
          {submenus.map((item) => {
            const Icon = item.icon;
            const isActive = activeSubmenu === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveSubmenu(item.id)}
                className={`px-3 py-2 rounded-xl text-xs font-bold font-sc transition-all flex items-center space-x-1.5 shrink-0 cursor-pointer ${
                  isActive 
                    ? 'bg-[#0B2644] text-white shadow-xs border border-[#7A470E]' 
                    : 'bg-white hover:bg-slate-200 text-slate-700 border border-slate-200'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300' : 'text-slate-500'}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Body based on selected Submenu */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-slate-800 bg-slate-50/50">
          
          {/* =================================================== */}
          {/* SUBMENU 1: ACADEMIC PROFILE & IDENTITY              */}
          {/* =================================================== */}
          {activeSubmenu === 'profile' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block border-b border-slate-100 pb-2">
                  Institutional Identity & Enrollment Credentials
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Candidate Full Name:
                    </span>
                    <strong className="text-[#0B2644] font-sc text-sm font-black block">{candidateName}</strong>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Matriculation Number:
                    </span>
                    <strong className="text-[#7A470E] font-mono text-sm font-black block">{student.matricNo}</strong>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Academic Programme:
                    </span>
                    <span className="font-bold text-slate-900 font-sc text-xs block">{candidateProgramme}</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Academic Session:
                    </span>
                    <span className="font-bold text-slate-900 font-mono text-xs block">October 2026 Commencement · 2026/2027</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Assigned Department:
                    </span>
                    <span className="font-bold text-slate-900 font-sc text-xs block">{student.department || "Department of Business Administration"}</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Study Modality:
                    </span>
                    <span className="font-bold text-slate-900 font-sc text-xs block">Executive Weekend Hybrid / Virtual-Immersive</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Campus Headquarters:
                    </span>
                    <span className="font-bold text-slate-900 font-sc text-xs block">Main Campus, Ikot Akpaden & Uyo Annex</span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-emerald-50/80 border border-emerald-300">
                    <span className="text-emerald-900 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Senate Enrollment Status:
                    </span>
                    <span className="inline-flex items-center text-emerald-800 font-black font-sc text-xs">
                      <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600 shrink-0" />
                      Confirmed & Cleared
                    </span>
                  </div>
                </div>
              </div>

              {/* Corporate Executive Profile */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc block border-b border-slate-100 pb-2">
                  Executive Corporate Affiliation
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 sm:col-span-2">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Industry Role / Designation:
                    </span>
                    <strong className="text-slate-900 font-sc font-bold block">Executive Management Practitioner · Industry Fellow</strong>
                  </div>
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Institutional Email:
                    </span>
                    <span className="font-mono text-[#0B2644] font-bold text-xs block">{candidateEmail}</span>
                  </div>
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                    <span className="text-slate-600 block text-[11px] font-sc font-bold uppercase tracking-wider mb-1">
                      Dispatch Phone & SMS:
                    </span>
                    <span className="font-mono text-slate-900 font-bold text-xs block">{candidatePhone}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* =================================================== */}
          {/* SUBMENU 2: BURSARY & TUITION CLEARANCE              */}
          {/* =================================================== */}
          {activeSubmenu === 'bursary' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* Financial Clearance Card */}
              <div className="p-5 bg-gradient-to-br from-emerald-950 via-[#0B2644] to-[#030F1C] text-white rounded-2xl border-2 border-emerald-500/50 shadow-md space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase font-mono tracking-widest text-emerald-300 font-bold flex items-center">
                    <ShieldCheck className="w-4 h-4 mr-1.5 text-emerald-400" />
                    Official Bursary Clearance Docket
                  </span>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500 text-slate-950 font-bold font-mono">
                    100% Cleared
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[11px]">Session Tuition Fee:</span>
                    <strong className="text-lg font-mono text-amber-300 font-bold">₦2,000,000</strong>
                    <span className="text-[10px] text-slate-400 block">DBA Terminal Doctorate</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[11px]">Acceptance Fee:</span>
                    <strong className="text-lg font-mono text-emerald-300 font-bold">₦50,000 Paid</strong>
                    <span className="text-[10px] text-slate-400 block">Ref: PSTK_LIVE_331902488</span>
                  </div>
                </div>

                <div className="pt-2 text-[11px] text-emerald-200/90 leading-relaxed">
                  ✓ Bursary verification complete. All LMS courseware, live seminar channels, and capstone assessment desks are fully unrestricted under Senate clearance rules.
                </div>
              </div>

              {/* Transaction Ledger Table */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                    Verified Payment Ledger
                  </span>
                  <button 
                    onClick={() => alert("Downloading official University Bursary Statement of Account (PDF)...")}
                    className="text-xs text-[#7A470E] hover:underline font-bold flex items-center space-x-1 cursor-pointer"
                  >
                    <Download className="w-3 h-3" />
                    <span>Download PDF Receipt</span>
                  </button>
                </div>

                <div className="space-y-2 text-xs">
                  {[
                    { item: "Session Tuition Fee (100% Full)", ref: "PSTK_LIVE_998471203", date: "15 Oct 2026", amount: "₦2,000,000", status: "Verified & Settled" },
                    { item: "Doctoral Acceptance & Matriculation Docket", ref: "PSTK_LIVE_331902488", date: "12 Oct 2026", amount: "₦50,000", status: "Verified & Settled" },
                    { item: "Executive Application & Screening Fee", ref: "PSTK_LIVE_110948332", date: "02 Oct 2026", amount: "₦25,000", status: "Verified & Settled" }
                  ].map((tx, idx) => (
                    <div key={idx} className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                      <div>
                        <strong className="text-slate-900 block font-sc">{tx.item}</strong>
                        <span className="text-[10px] text-slate-400 font-mono">{tx.ref} · {tx.date}</span>
                      </div>
                      <div className="text-right">
                        <strong className="text-slate-900 font-mono block">{tx.amount}</strong>
                        <span className="text-[10px] text-emerald-600 font-bold">● {tx.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* =================================================== */}
          {/* SUBMENU 3: ACADEMIC RECORDS & GRADES                */}
          {/* =================================================== */}
          {activeSubmenu === 'grades' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* GPA & Credit Meter */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3.5 bg-white rounded-2xl border border-slate-200 text-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase font-mono block">Cumulative GPA</span>
                  <strong className="text-xl font-mono text-[#0B2644] font-black">4.85</strong>
                  <span className="text-[10px] text-emerald-600 font-bold block">First Class Standing</span>
                </div>
                <div className="p-3.5 bg-white rounded-2xl border border-slate-200 text-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase font-mono block">Credits Completed</span>
                  <strong className="text-xl font-mono text-[#7A470E] font-black">6 / 36</strong>
                  <span className="text-[10px] text-slate-500 block">Term 1 Enrolled</span>
                </div>
                <div className="p-3.5 bg-white rounded-2xl border border-slate-200 text-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase font-mono block">Senate Status</span>
                  <strong className="text-sm font-sc text-emerald-700 font-black block mt-1">Cleared</strong>
                  <span className="text-[10px] text-slate-400 block">Viva Track Active</span>
                </div>
              </div>

              {/* Module Assessment Breakdown */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                    Term 1 Continuous Assessment Breakdown
                  </span>
                  <span className="text-[11px] font-mono text-slate-500">DBA 911 / MGT 801</span>
                </div>

                <div className="space-y-2 text-xs">
                  {[
                    { component: "Continuous Assessment 1 (Methodology Framework Essay)", score: "28 / 30", grade: "A (Distinction)", date: "16 Oct 2026" },
                    { component: "Harvard Case Analysis (Turnaround Strategy Deliverable)", score: "37 / 40", grade: "A (Distinction)", date: "18 Oct 2026" },
                    { component: "Syndicate Boardroom Presentation & Colloquium", score: "Scheduled", grade: "Pending Term End", date: "Nov 2026" }
                  ].map((res, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
                      <div>
                        <strong className="text-slate-900 block font-sc">{res.component}</strong>
                        <span className="text-[10px] text-slate-400 font-mono">Submitted: {res.date}</span>
                      </div>
                      <div className="text-right">
                        <strong className="text-[#0B2644] font-mono text-sm block">{res.score}</strong>
                        <span className="text-[10px] font-bold text-amber-700">{res.grade}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* =================================================== */}
          {/* SUBMENU 4: DISSERTATION & SUPERVISORY DESK          */}
          {/* =================================================== */}
          {activeSubmenu === 'supervisor' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block border-b border-slate-100 pb-2">
                  Assigned Doctoral Supervisory Committee
                </span>

                <div className="space-y-3 text-xs">
                  <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-200/80 flex items-start space-x-3">
                    <BookOpen className="w-5 h-5 text-[#7A470E] shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] font-mono uppercase text-[#7A470E] font-bold block">Lead Academic Supervisor:</span>
                      <strong className="text-slate-900 text-sm font-sc block">Prof. Asuquo E. Johnson, PhD, FNIM, FIMC</strong>
                      <span className="text-[11px] text-slate-600 block">Professor of Strategic Management & Corporate Governance</span>
                      <a href="mailto:prof.johnson@businessschool.aksu.edu.ng" className="text-[10px] text-[#7A470E] hover:underline font-mono mt-1 inline-block">
                        prof.johnson@businessschool.aksu.edu.ng
                      </a>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start space-x-3">
                    <ShieldCheck className="w-5 h-5 text-[#0B2644] shrink-0 mt-0.5" />
                    <div>
                      <span className="text-[10px] font-mono uppercase text-slate-500 font-bold block">Co-Supervisor / Directorate Oversight:</span>
                      <strong className="text-slate-900 text-sm font-sc block">Dr Uwem Etim Uwah, PhD, FCA, ACTI</strong>
                      <span className="text-[11px] text-slate-600 block">Director, AKSU Business School · Associate Professor of Accounting</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dissertation Milestones */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc block border-b border-slate-100 pb-2">
                  Approved Dissertation Working Title & Milestones
                </span>
                
                <p className="text-xs italic text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  "Strategic Corporate Turnaround, Boardroom Governance, and Operational Resilience Paradigms in Sub-Saharan Energy Conglomerates"
                </p>

                <div className="space-y-2 text-xs pt-1">
                  {[
                    { step: "Proposal Defence & Methodology Approval", status: "Approved & Cleared", state: "done" },
                    { step: "Institutional Ethics Committee (REC) Clearance", status: "Verified (REC-2026-081)", state: "done" },
                    { step: "Empirical Fieldwork & Corporate Data Collection", status: "In Progress (Term 1)", state: "current" },
                    { step: "Draft Dissertation Review by Supervisory Committee", status: "Scheduled for Term 2", state: "pending" },
                    { step: "External Examiner Senate Viva Voce", status: "Terminal Milestone", state: "pending" }
                  ].map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 bg-white">
                      <div className="flex items-center space-x-2">
                        {m.state === 'done' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        ) : m.state === 'current' ? (
                          <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0 ml-1 mr-0.5" />
                        ) : (
                          <Clock className="w-4 h-4 text-slate-300 shrink-0" />
                        )}
                        <span className="font-semibold text-slate-800">{m.step}</span>
                      </div>
                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                        m.state === 'done' ? 'bg-emerald-100 text-emerald-800' :
                        m.state === 'current' ? 'bg-amber-100 text-[#7A470E]' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {m.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* =================================================== */}
          {/* SUBMENU 5: DIGITAL ID CARD & QR PASS                */}
          {/* =================================================== */}
          {activeSubmenu === 'idcard' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* Holographic Virtual Student Card */}
              <div className="bg-gradient-to-br from-[#030F1C] via-[#0B2644] to-[#7A470E] text-white p-6 rounded-3xl border-2 border-[#B87A28] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#B87A28]/20 rounded-full blur-2xl pointer-events-none" />

                <div className="flex items-center justify-between pb-4 border-b border-white/20">
                  <div className="flex items-center space-x-3">
                    <img src="/Logo.png" alt="AKSU Crest" className="w-10 h-10 object-contain logo-glow" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-amber-300 font-bold">
                        Akwa Ibom State University
                      </div>
                      <div className="text-sm font-black font-sc text-white">
                        Executive Business School · Student ID
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] bg-[#7A470E] text-white px-2 py-0.5 rounded font-mono font-bold border border-amber-300/40">
                    2026/2027
                  </span>
                </div>

                <div className="flex items-center justify-between pt-5 gap-4">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={student.avatar} 
                      alt={candidateName} 
                      className="w-16 h-16 rounded-xl object-cover border-2 border-white/80 shadow-md"
                    />
                    <div className="space-y-0.5">
                      <div className="text-base font-black font-sc text-white">{candidateName}</div>
                      <div className="text-xs font-mono text-amber-300 font-bold">{student.matricNo}</div>
                      <div className="text-xs text-amber-100 font-sc">{candidateProgramme}</div>
                      <div className="text-[10px] text-emerald-300 font-mono font-bold">Status: Full Senate Clearance</div>
                    </div>
                  </div>

                  {/* QR Code Graphic */}
                  <div className="bg-white p-2 rounded-xl border border-white/40 shrink-0 text-center shadow-lg">
                    <QrCode className="w-14 h-14 text-slate-900 mx-auto" />
                    <span className="text-[8px] font-mono text-slate-600 block mt-0.5">GATE PASS</span>
                  </div>
                </div>

                <div className="mt-5 pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-slate-300">
                  <span>Main Campus & Uyo Executive Suites</span>
                  <span>Valid Thru: Oct 2029</span>
                </div>
              </div>

              {/* Mobile Companion Token */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc flex items-center">
                    <Smartphone className="w-4 h-4 mr-1.5 text-[#7A470E]" />
                    Mobile LMS Offline Token (Scan to Sync)
                  </span>
                  <button
                    onClick={handleCopyQRToken}
                    className="text-xs text-[#7A470E] hover:underline font-bold cursor-pointer"
                  >
                    {copiedToken ? '✓ Token Copied' : 'Copy Token'}
                  </button>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Open the AKSU Business School Companion App on your mobile device and scan this secure QR pass to access your syllabus, audio summaries, and offline lecture notes.
                </p>
              </div>
            </div>
          )}

          {/* =================================================== */}
          {/* SUBMENU 6: SECURITY, 2FA & PRIVACY                  */}
          {/* =================================================== */}
          {activeSubmenu === 'security' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs space-y-4">
                <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc block border-b border-slate-100 pb-2">
                  Account Access Security & RBAC Scoping
                </span>

                <div className="space-y-3 text-xs">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <strong className="text-slate-900 block font-sc">Two-Factor Authentication (2FA)</strong>
                      <span className="text-[11px] text-slate-500">Require SMS / Authenticator app verification upon login</span>
                    </div>
                    <button
                      onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                      className={`px-3 py-1 rounded-full text-xs font-bold font-mono transition-colors cursor-pointer ${
                        twoFactorEnabled ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                      }`}
                    >
                      {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <strong className="text-slate-900 block font-sc">WhatsApp & SMS Academic Alerts</strong>
                      <span className="text-[11px] text-slate-500">Receive timetable changes, supervisory notes & grading notices</span>
                    </div>
                    <button
                      onClick={() => setWhatsappAlerts(!whatsappAlerts)}
                      className={`px-3 py-1 rounded-full text-xs font-bold font-mono transition-colors cursor-pointer ${
                        whatsappAlerts ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'
                      }`}
                    >
                      {whatsappAlerts ? 'Active' : 'Muted'}
                    </button>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <strong className="text-slate-900 block font-sc mb-1">Active Session Information</strong>
                    <div className="font-mono text-[11px] text-slate-600 space-y-0.5">
                      <div>Terminal: Windows 11 Chrome Desktop (Current Device)</div>
                      <div>IP Address: 102.89.44.12 · Uyo, Akwa Ibom State, Nigeria</div>
                      <div>Security Tier: Tier-3 Enrolled Scholar (Least Privilege Active)</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sign Out Button */}
              <button
                onClick={() => {
                  onClose();
                  if (onLogout) onLogout();
                }}
                className="w-full py-3 px-4 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold font-sc transition-colors flex items-center justify-center space-x-2 cursor-pointer"
              >
                <span>Terminate Scholar Session & Return to Portal Hub</span>
              </button>
            </div>
          )}

        </div>

        {/* Drawer Footer */}
        <div className="p-4 bg-slate-100 border-t border-slate-200 flex items-center justify-between text-xs shrink-0 text-slate-500 font-mono">
          <span>AKSU Business School · ISO 27001 Certified</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-[#0B2644] text-white hover:bg-[#7A470E] font-sc font-bold transition-colors cursor-pointer"
          >
            Close Menu
          </button>
        </div>

      </div>
    </div>
  );
};

export default CandidateAccountDrawer;
